/**
 * CallKit Integration for iOS
 *
 * CallKit provides native iOS call UI for VoIP apps. This integration allows:
 * - Incoming call notifications on lock screen (like native phone calls)
 * - Call handling from Control Center and CarPlay
 * - Do Not Disturb integration
 * - Caller ID display
 *
 * Required for iOS VoIP apps that use PushKit (VoIP push notifications).
 * Apple requires CallKit integration when using VoIP background mode.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

// CallKit plugin interface
export interface CallKitPlugin {
  setup(options: CallKitSetupOptions): Promise<void>;
  reportIncomingCall(options: IncomingCallOptions): Promise<{ uuid: string }>;
  reportCallStarted(options: { uuid: string }): Promise<void>;
  reportCallEnded(options: CallEndOptions): Promise<void>;
  reportCallConnected(options: { uuid: string }): Promise<void>;
  reportCallOnHold(options: { uuid: string; onHold: boolean }): Promise<void>;
  setMuted(options: { uuid?: string; muted: boolean }): Promise<void>;
  addListener(
    event: 'callAnswered',
    callback: (data: CallAnsweredEvent) => void,
  ): Promise<void>;
  addListener(
    event: 'callEnded',
    callback: (data: CallEndedEvent) => void,
  ): Promise<void>;
  addListener(
    event: 'callStarted',
    callback: (data: CallStartedEvent) => void,
  ): Promise<void>;
  addListener(
    event: 'callMuted',
    callback: (data: CallMutedEvent) => void,
  ): Promise<void>;
}

export interface CallKitSetupOptions {
  appName: string;
  ringtoneSound?: string;
  iconTemplateImageName?: string;
  maximumCallGroups?: number;
  maximumCallsPerCallGroup?: number;
  supportsVideo?: boolean;
}

export interface IncomingCallOptions {
  uuid: string;
  handle: string;
  handleType: 'generic' | 'phoneNumber' | 'email';
  localizedCallerName: string;
  hasVideo?: boolean;
  supportsHolding?: boolean;
  supportsDTMF?: boolean;
  supportsGrouping?: boolean;
  supportsUngrouping?: boolean;
}

export interface CallEndOptions {
  uuid: string;
  reason:
    | 'failed'
    | 'remoteEnded'
    | 'unanswered'
    | 'answeredElsewhere'
    | 'declinedElsewhere';
}

export interface CallAnsweredEvent {
  uuid: string;
}

export interface CallEndedEvent {
  uuid: string;
  action: 'end' | 'decline';
}

export interface CallStartedEvent {
  uuid: string;
  handle: string;
}

export interface CallMutedEvent {
  uuid: string;
  muted: boolean;
}

// Active call tracking
interface ActiveCall {
  uuid: string;
  callSid?: string;
  callerName: string;
  callerNumber: string;
  isIncoming: boolean;
  startTime: Date;
}

let callKitPlugin: CallKitPlugin | null = null;
let activeCalls: Map<string, ActiveCall> = new Map();
let callHandlers: {
  onAnswer?: (callId: string) => void;
  onEnd?: (callId: string) => void;
  onDecline?: (callId: string) => void;
  onMute?: (callId: string, muted: boolean) => void;
  /** PushKit VoIP token — forward to registerDevice() as `voipToken`. */
  onVoipToken?: (token: string) => void;
  /** A VoIP push arrived; CallKit has ALREADY shown the OS call UI. */
  onIncomingVoipPush?: (payload: Record<string, unknown>) => void;
} = {};

/**
 * Check if CallKit is available
 */
export function isCallKitAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * Initialize CallKit
 */
export async function initializeCallKit(handlers?: typeof callHandlers): Promise<void> {
  if (!isCallKitAvailable()) {
    console.log('CallKit is only available on iOS');
    return;
  }

  if (handlers) {
    callHandlers = handlers;
  }

  try {
    // Try to load the CallKit plugin
    // This requires @nichetech/capacitor-callkit or similar plugin
    callKitPlugin = registerPlugin<CallKitPlugin>('CallKit');

    await callKitPlugin.setup({
      appName: 'InsuraCentral',
      ringtoneSound: 'ringback.mp3',
      supportsVideo: false,
      maximumCallGroups: 1,
      maximumCallsPerCallGroup: 1,
    });

    // Set up event listeners
    callKitPlugin.addListener('callAnswered', (event) => {
      console.log('CallKit: Call answered', event.uuid);
      callHandlers.onAnswer?.(event.uuid);
    });

    callKitPlugin.addListener('callEnded', (event) => {
      console.log('CallKit: Call ended', event.uuid, event.action);
      if (event.action === 'decline') {
        callHandlers.onDecline?.(event.uuid);
      } else {
        callHandlers.onEnd?.(event.uuid);
      }
      activeCalls.delete(event.uuid);
    });

    // ⚠️ This is the event that closes the loop. Without forwarding this
    // token to the backend, VoipPushService finds no eligible iOS device and
    // silently sends nothing.
    (callKitPlugin as any).addListener('voipTokenReceived', (event: { token: string }) => {
      if (event?.token) callHandlers.onVoipToken?.(event.token);
    });

    (callKitPlugin as any).addListener('incomingVoipPush', (payload: Record<string, unknown>) => {
      callHandlers.onIncomingVoipPush?.(payload);
    });

    callKitPlugin.addListener('callMuted', (event) => {
      console.log('CallKit: Call muted', event.uuid, event.muted);
      callHandlers.onMute?.(event.uuid, event.muted);
    });

    console.log('CallKit initialized successfully');
  } catch (error) {
    console.warn('CallKit plugin not available:', error);
    // CallKit not available, will fall back to in-app UI
  }
}

/**
 * Report an incoming call to CallKit
 * This will show the native iOS call UI
 */
export async function reportIncomingCall(
  callId: string,
  callerName: string,
  callerNumber: string,
  callSid?: string,
): Promise<string | null> {
  if (!callKitPlugin) {
    console.log('CallKit not initialized, using in-app UI');
    return null;
  }

  try {
    // Generate a UUID for CallKit
    const uuid = crypto.randomUUID();

    await callKitPlugin.reportIncomingCall({
      uuid,
      handle: callerNumber,
      handleType: 'phoneNumber',
      localizedCallerName: callerName || callerNumber,
      hasVideo: false,
      supportsHolding: true,
      supportsDTMF: true,
    });

    // Track the active call
    activeCalls.set(uuid, {
      uuid,
      callSid,
      callerName,
      callerNumber,
      isIncoming: true,
      startTime: new Date(),
    });

    console.log('Reported incoming call to CallKit:', uuid);
    return uuid;
  } catch (error) {
    console.error('Failed to report incoming call to CallKit:', error);
    return null;
  }
}

/**
 * Report that a call was connected
 */
export async function reportCallConnected(uuid: string): Promise<void> {
  if (!callKitPlugin || !uuid) return;

  try {
    await callKitPlugin.reportCallConnected({ uuid });
    console.log('Reported call connected to CallKit:', uuid);
  } catch (error) {
    console.error('Failed to report call connected:', error);
  }
}

/**
 * Report that a call has ended
 */
export async function reportCallEnded(
  uuid: string,
  reason: CallEndOptions['reason'] = 'remoteEnded',
): Promise<void> {
  if (!callKitPlugin || !uuid) return;

  try {
    await callKitPlugin.reportCallEnded({ uuid, reason });
    activeCalls.delete(uuid);
    console.log('Reported call ended to CallKit:', uuid, reason);
  } catch (error) {
    console.error('Failed to report call ended:', error);
  }
}

/**
 * Report an outgoing call started
 */
export async function reportOutgoingCallStarted(
  callerNumber: string,
  callSid?: string,
): Promise<string | null> {
  if (!callKitPlugin) {
    return null;
  }

  try {
    const uuid = crypto.randomUUID();

    await callKitPlugin.reportCallStarted({ uuid });

    activeCalls.set(uuid, {
      uuid,
      callSid,
      callerName: '',
      callerNumber,
      isIncoming: false,
      startTime: new Date(),
    });

    console.log('Reported outgoing call started to CallKit:', uuid);
    return uuid;
  } catch (error) {
    console.error('Failed to report outgoing call:', error);
    return null;
  }
}

/**
 * Get active call by UUID or call SID
 */
export function getActiveCall(identifier: string): ActiveCall | undefined {
  // Check by UUID first
  if (activeCalls.has(identifier)) {
    return activeCalls.get(identifier);
  }

  // Check by call SID
  for (const call of activeCalls.values()) {
    if (call.callSid === identifier) {
      return call;
    }
  }

  return undefined;
}

/**
 * Get all active calls
 */
export function getActiveCalls(): ActiveCall[] {
  return Array.from(activeCalls.values());
}

/**
 * Reverse mute sync — mirror the IN-APP mute button onto the lock-screen
 * call card. uuid optional: inbound calls' UUID lives natively, the plugin
 * falls back to the first active call.
 */
export async function setCallKitMuted(muted: boolean, uuid?: string): Promise<void> {
  if (!callKitPlugin) return;
  try {
    await callKitPlugin.setMuted({ uuid, muted });
  } catch {
    /* cosmetic mirror — never let it affect the call */
  }
}
